import React, { useState, useMemo } from 'react';
import MarketingLayout from '../components/MarketingLayout';
import {
  Box,
  Container,
  Typography,
  TextField,
  Grid,
  Card,
  CardContent,
  Chip,
  InputAdornment,
  Tabs,
  Tab,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { glossary, glossaryCategories } from '../data/glossaryContent';

const Glossary = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  const handleCategoryChange = (event, newValue) => {
    setSelectedCategory(newValue);
  };

  const filteredTerms = useMemo(() => {
    let filtered = glossary;

    // Filter by category
    if (selectedCategory !== 'all') {
      const category = glossaryCategories.find((cat) => cat.name === selectedCategory);
      if (category) {
        filtered = glossary.filter((item) => category.terms.includes(item.term));
      }
    }

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(
        (item) =>
          item.term.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.definition.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    return filtered.sort((a, b) => a.term.localeCompare(b.term));
  }, [searchTerm, selectedCategory]);

  return (
    <MarketingLayout>
    <Box sx={{ backgroundColor: '#f8f9fa', minHeight: '100vh', py: 8 }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography variant="h2" component="h1" gutterBottom sx={{ fontWeight: 700, color: '#052d50' }}>
            Investment Glossary
          </Typography>
          <Typography variant="h6" color="textSecondary" sx={{ mb: 4 }}>
            Key venture capital and investment terms explained
          </Typography>

          {/* Search Bar */}
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search terms or definitions..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{
              maxWidth: 600,
              mx: 'auto',
              backgroundColor: 'white',
              borderRadius: 2,
              '& .MuiOutlinedInput-root': {
                '&:hover fieldset': { borderColor: '#478289' },
                '&.Mui-focused fieldset': { borderColor: '#478289' },
              },
            }}
          />
        </Box>

        {/* Category Tabs */}
        <Box sx={{ mb: 4, borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={selectedCategory}
            onChange={handleCategoryChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              '& .MuiTab-root': { fontWeight: 600, fontSize: '1rem' },
              '& .Mui-selected': { color: '#478289' },
              '& .MuiTabs-indicator': { backgroundColor: '#478289' },
            }}
          >
            <Tab label="All Terms" value="all" />
            {glossaryCategories.map((category) => (
              <Tab label={category.name} value={category.name} key={category.name} />
            ))}
          </Tabs>
        </Box>

        {/* Results Count */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="textSecondary">
            Showing {filteredTerms.length} {filteredTerms.length === 1 ? 'term' : 'terms'}
          </Typography>
        </Box>

        {/* Glossary Terms */}
        {filteredTerms.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h6" color="textSecondary">
              No terms found matching your search.
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={3}>
            {filteredTerms.map((item, index) => {
              // Find which category this term belongs to
              const termCategory = glossaryCategories.find((cat) => cat.terms.includes(item.term));

              return (
                <Grid item xs={12} key={index}>
                  <Card
                    sx={{
                      transition: 'all 0.3s',
                      '&:hover': { boxShadow: 4, transform: 'translateY(-2px)' },
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
                        <Typography variant="h5" component="h2" sx={{ fontWeight: 600, color: '#052d50' }}>
                          {item.term}
                        </Typography>
                        {termCategory && (
                          <Chip
                            label={termCategory.name}
                            size="small"
                            sx={{
                              backgroundColor: '#e3f2fd',
                              color: '#478289',
                              fontWeight: 500,
                            }}
                          />
                        )}
                      </Box>
                      <Typography variant="body1" sx={{ lineHeight: 1.8, color: 'text.secondary' }}>
                        {item.definition}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}

        {/* Educational Note */}
        <Box
          sx={{
            mt: 8,
            p: 4,
            backgroundColor: 'white',
            borderRadius: 2,
            borderLeft: '4px solid #478289',
          }}
        >
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
            New to Venture Capital?
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Understanding these terms is crucial for making informed investment decisions. We recommend familiarizing
            yourself with key concepts before making your first investment. Our team is always available to answer
            questions and provide guidance.
          </Typography>
        </Box>
      </Container>
    </Box>
  
    </MarketingLayout>
  );
};

export default Glossary;
